import React, { useMemo } from 'react';
import BoltIcon from '@mui/icons-material/Bolt';
import { Box, type SxProps, type Theme } from '@mui/material';

import type { ActionButton } from '../components/EntitlementBlockedOverlay';
import { EntitlementBlockedOverlay } from '../components/EntitlementBlockedOverlay';
import { useEntitlementUpgradeAction } from '../hooks/useEntitlementUpgradeAction';
import { useEntitlement } from '../store/api';
import {
  formatCapabilityName,
  getUsagePeriodLabel,
  isDepleted,
} from '../utils/entitlementFormatters';

export interface EntitlementGateProps {
  /** The capability name to check */
  capabilityName: string;

  /** Children to render */
  children: React.ReactNode;

  /** Source for analytics tracking */
  source?: string;

  /** Override default title */
  title?: string;

  /** Override default description */
  description?: string;

  /** Override primary action */
  primaryAction?: ActionButton;

  /** Optional secondary action */
  secondaryAction?: ActionButton;

  /** Callback when upgrade is clicked (used for default primary action) */
  onUpgradeClick?: () => void;

  /** Optional sx prop for the wrapper Box */
  sx?: SxProps<Theme>;

  /** Optional sx prop for the overlay */
  overlaySx?: SxProps<Theme>;
}

/**
 * EntitlementGate Component
 *
 * Wrapper component that conditionally shows an EntitlementBlockedOverlay
 * when the user's entitlement quota is depleted.
 *
 * Behavior:
 * - If feature gate is not enabled: renders children as-is
 * - If entitlement not found: renders children as-is
 * - If entitlement is not depleted: renders children as-is
 * - If entitlement is depleted: wraps children in relative div with overlay
 *
 * Auto-generates default overlay content based on entitlement state,
 * but allows prop overrides for title, description, and actions.
 *
 * @example
 * ```tsx
 * <EntitlementGate
 *   capabilityName="notes"
 *   source="encounter_create"
 *   onUpgradeClick={() => handleUpgrade()}
 * >
 *   <CreateEncounterForm />
 * </EntitlementGate>
 * ```
 */
export const EntitlementGate = React.memo<EntitlementGateProps>(
  ({
    capabilityName,
    children,
    source,
    title,
    description,
    primaryAction,
    secondaryAction,
    onUpgradeClick,
    sx,
    overlaySx,
  }) => {
    // Get entitlement using dedicated entitlements endpoint
    // refetchOnMountOrArgChange ensures fresh data when component mounts (e.g., after note generation)
    // refetchOnFocus ensures fresh data when user returns to the browser tab
    const { entitlement: fetchedEntitlement } = useEntitlement(capabilityName, {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
    });

    // TODO: Re-enable clinician role check after backend entitlement change
    const isClinician = true; // Temporarily default to true

    // Shared upgrade handler: solo admin → modal, group admin → clinicians page, non-admin → contact admin
    const { handleUpgradeClick: defaultUpgradeClick } = useEntitlementUpgradeAction({
      capabilityName,
      bundleId: fetchedEntitlement?.bundle_id,
    });
    const handleUpgradeClick = onUpgradeClick || defaultUpgradeClick;

    const entitlement = fetchedEntitlement;

    // Render children wrapped in relative div with overlay
    const wrapperSx = useMemo(() => {
      if (!sx) {
        return { position: 'relative' };
      }
      return [{ position: 'relative' }, sx] as SxProps<Theme>;
    }, [sx]);

    // If entitlement not found, render children as-is
    if (!entitlement) {
      return <>{children}</>;
    }

    // Default primary action for all blocked states
    const defaultPrimaryAction: ActionButton = {
      label: 'Request Upgrade',
      onClick: handleUpgradeClick,
      startIcon: <BoltIcon />,
    };

    // Non-clinician users: always show blocking overlay
    if (!isClinician) {
      return (
        <Box sx={wrapperSx}>
          {children}
          <EntitlementBlockedOverlay
            title="Feature Not Available"
            description="As an admin, you do not have access to this feature. To unlock, please contact your practice administrator to upgrade your seat."
            primaryAction={primaryAction}
            secondaryAction={secondaryAction}
            sx={overlaySx}
            trackingMetadata={{
              capability_name: entitlement.capability_name,
              source,
              has_quota: entitlement.quota !== null && entitlement.quota !== undefined,
              current_usage: entitlement.current_usage ?? undefined,
              quota: entitlement.quota ?? undefined,
            }}
          />
        </Box>
      );
    }

    // Check if entitlement is depleted
    const depleted = entitlement ? isDepleted(entitlement) : false;

    // If not depleted, render children as-is
    if (!depleted || !entitlement) {
      return <>{children}</>;
    }

    // Generate default overlay content for depleted state
    const period = getUsagePeriodLabel(entitlement.usage_period ?? null);
    const periodText = period ? `${period} ` : '';
    const defaultTitle = `You've reached your ${periodText}limit`;

    const capabilityDisplayName = formatCapabilityName(entitlement.capability_name);
    const quota = entitlement.quota ?? 0;
    const defaultDescription = `You've used all ${quota} ${capabilityDisplayName}. Upgrade for unlimited access.`;

    return (
      <Box sx={wrapperSx}>
        {children}
        <EntitlementBlockedOverlay
          title={title ?? defaultTitle}
          description={description ?? defaultDescription}
          primaryAction={primaryAction ?? defaultPrimaryAction}
          secondaryAction={secondaryAction}
          sx={overlaySx}
          trackingMetadata={{
            capability_name: entitlement.capability_name,
            source,
            has_quota: entitlement.quota !== null && entitlement.quota !== undefined,
            current_usage: entitlement.current_usage ?? undefined,
            quota: entitlement.quota ?? undefined,
          }}
        />
      </Box>
    );
  },
);

EntitlementGate.displayName = 'EntitlementGate';
